/* eslint-disable @typescript-eslint/no-explicit-any */
import { AiAgentMessage, ChatRequest, ChatResponse, ChatHistoryItem } from './ai-agent.type'
import { parseMarkdownToHtml } from './libs/markdown-parser'
import { Session } from '../../types/enums'
import auth from '../../services/auth'
import type { IPolicy } from '../../types'

interface SendMessageArgs {
  message: string
  history?: AiAgentMessage[]
  coverIds?: string[] | number[]
  insuranceClass?: string
  policyCodes?: string[] // Add policyCodes for old structure
  selectedPolicyContents?: any[]
  allPolicies?: any[]
  userId?: string
  policyIds?: string[] | number[]
  countryCode?: string
}

const INSIGHT_API_URLS = process.env.REACT_APP_INSIGHT_API_URL
const PCI_API_URLS = process.env.REACT_APP_BASE_API_URL

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Api-Version': 'v1',
    'x-api-key': process.env.REACT_APP_CHAT_API_KEY || '',
  }

  // Add Authorization header if token exists (guard for SSR)
  if (typeof window !== 'undefined') {
    const authToken = localStorage.getItem(Session.KEY)
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
  }

  return headers
}

// Auth header without content-type for multipart/form-data
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Api-Version': 'v1',
    'x-api-key': process.env.REACT_APP_CHAT_API_KEY || '',
  }
  if (typeof window !== 'undefined') {
    const authToken = localStorage.getItem(Session.KEY)
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
  }
  return headers
}

/**
 * Every axios call carries the impersonated buy group from the axios defaults, so the API
 * answers as that group. A fetch call gets no such thing, and the header is the only way
 * the request says which buy group it is for — without it an admin importing while
 * impersonating is answered for their own group instead.
 *
 * The id is set as a number, so it is stringified here: fetch drops nothing, but a number
 * is not a valid header value.
 */
function getImpersonationHeaders(): Record<string, string> {
  const buyGroup = auth.impersonateBuyGroup
  if (buyGroup === undefined || buyGroup === null || buyGroup === '') return {}
  return { 'x-impersonate-buy-group': String(buyGroup) }
}

function getEnvironment(): string {
  if (process.env.NODE_ENV === 'development') return 'dev'
  if (typeof window === 'undefined') return 'uat' // SSR fallback

  const hostname = window.location.hostname.toLowerCase()

  if (hostname.includes('featureaiagent')) {
    return 'dev'
  }

  // Treat only root domain as production
  if (hostname === 'policycomparison.com' || hostname === 'www.policycomparison.com') return 'prod'

  // Known non-prod subdomains
  if (
    hostname.startsWith('uat.') ||
    hostname.startsWith('staging.') ||
    hostname.includes('.uat.') ||
    hostname.includes('.staging.')
  )
    return 'uat'

  // For now, defaulting to 'uat' for other domains
  return 'uat'
}

function convertToApiHistory(messages: AiAgentMessage[] | undefined): ChatHistoryItem[] {
  const safeMessages = Array.isArray(messages) ? messages : []

  return safeMessages
    .filter((msg): msg is AiAgentMessage => !!msg && !!msg.sender && typeof msg.text === 'string')
    .filter((msg) => msg.sender === 'user' || msg.sender === 'assistant')
    .map((msg) => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.text,
    }))
}

async function sendMessageToAgent({
  message,
  history = [],
  coverIds = [],
  selectedPolicyContents = [],
  allPolicies = [],
  userId,
  policyIds,
  countryCode,
}: SendMessageArgs): Promise<AiAgentMessage> {
  try {
    const apiHistory = convertToApiHistory(history)
    const endpointUrl = `${INSIGHT_API_URLS}PolicyInsight/Chat`

    const requestBody: ChatRequest = {
      // New structure for dev/uat
      query: message,
      history: apiHistory,
      coverIds: coverIds,
      userId,
      policyIds,
      countryCode,
    }

    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    })

    // Handle explicit rate limiting (429)
    if (response.status === 429) {
      const rateLimitReset = response.headers.get('RateLimit-Reset')
      let waitSeconds: number | null = null

      // RateLimit-Reset when present (IETF proposal)
      if (rateLimitReset) {
        const n = Number(rateLimitReset)
        if (!Number.isNaN(n)) waitSeconds = Math.max(0, Math.ceil(n))
      }

      const msg =
        waitSeconds !== null && waitSeconds > 0
          ? `Rate limit reached. Please wait ${waitSeconds}s and try again.`
          : 'Rate limit reached. Please wait a moment and try again.'

      return {
        id: Date.now().toString(),
        sender: 'assistant',
        text: msg,
        parsedHtml: msg,
        timestamp: Date.now(),
      }
    }

    if (!response.ok) {
      throw new Error(`API request failed with status: ${response.status}`)
    }

    const data: ChatResponse = await response.json()

    const responseText = data.Answer || data.answer || 'Sorry, I could not process your request.'

    // Parse HTML once when receiving the response
    const parsedHtml = parseMarkdownToHtml(responseText, selectedPolicyContents, allPolicies)

    return {
      id: Date.now().toString(),
      sender: 'assistant',
      text: responseText,
      parsedHtml, // Store the parsed HTML
      timestamp: Date.now(),
    }
  } catch (error) {
    console.error('Error sending message to AI agent:', error)

    // Return a fallback message on error
    return {
      id: Date.now().toString(),
      sender: 'assistant',
      text: 'Sorry, I encountered an error while processing your request. Please try again.',
      parsedHtml: 'Sorry, I encountered an error while processing your request. Please try again.',
      timestamp: Date.now(),
    }
  }
}

export { sendMessageToAgent, getEnvironment }

// Email AI response
interface SendAiEmailArgs {
  to: string
  cc?: string
  subject: string
  html?: string
  text?: string
}

async function sendAiResponseEmail({ to, cc, subject, html, text }: SendAiEmailArgs) {
  const apiUrl = `${INSIGHT_API_URLS}PolicyInsight/Email`
  const body = {
    to,
    cc,
    subject,
    html,
    text,
  }

  const headers = getHeaders()

  const res = await fetch(`${apiUrl}PolicyInsight/Email`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to send email')
}

export { sendAiResponseEmail }

// Generate "Key Differences" PDF by uploading CSV to the Reports API
async function requestComparisonPdfFromCsv({
  csvContent,
  filename = 'input.csv',
  format = 'Pdf',
}: {
  csvContent: string
  filename?: string
  reportsApiBase?: string
  format?: 'Pdf' | 'Docx'
}): Promise<Blob> {
  const endpoint = `${PCI_API_URLS}reports/comparison-feature`

  const form = new FormData()
  const csvBlob = new Blob([csvContent], { type: 'text/csv' })
  form.append('input', csvBlob, filename)
  form.append('format', format)

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: form,
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Report generation failed (${res.status}): ${text || 'Unknown error'}`)
  }

  return await res.blob()
}

export { requestComparisonPdfFromCsv }

export interface UploadedPolicyCheckResult {
  fileName: string
  policyCode?: string
  status: 'matched' | 'not_found' | 'unprocessable' | 'rejected'
  message: string
  followUpQuestion?: string
}

interface HashLookupResponse {
  content_hash: string
  exists: boolean
  uploaded_filename: string
  matched_file_path: string | null
  reviewed: string
  file_last_modified: string
  created_at: string
  is_fuzzy_match: boolean
  similarity_score: number | null
  fuzzy_matched_path: string | null
  is_archived?: boolean
}

async function uploadPolicyPdf(file: File): Promise<UploadedPolicyCheckResult> {
  const endpoint = 'https://pdf-inspector-app.azurewebsites.net/hashes/lookup'

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`Upload failed (${response.status}): ${errorText}`)
    }

    const data = (await response.json().catch(() => null)) as HashLookupResponse[] | null

    // Handle response - API returns an array of results
    if (!Array.isArray(data) || data.length === 0) {
      return {
        fileName: file.name,
        status: 'not_found',
        message: 'No matching policy found. Please verify the wording or try another file.',
      }
    }

    // Get the first result (since we're uploading one file at a time)
    const result = data[0]

    // Check if the file exists in the system
    if (result.exists === true) {
      return {
        fileName: result.uploaded_filename || file.name,
        status: 'matched',
        message: result.is_archived ? 'Policy matched (Archived)' : 'Policy matched (Active)',
      }
    }

    // File does not exist
    return {
      fileName: result.uploaded_filename || file.name,
      status: 'not_found',
      message: 'Policy not found',
    }
  } catch (error) {
    console.error('Error uploading policy PDF:', error)
    throw error
  }
}

export { uploadPolicyPdf }

export type PolicyImportResult =
  | { status: 'found'; policies: IPolicy[]; fileName: string }
  | { status: 'not_found'; fileName: string }
  | { status: 'unprocessable'; fileName: string }
  /** Refused on a rule the user can act on, e.g. the wording is for another country. */
  | { status: 'rejected'; fileName: string; detail: string }

/** RFC 9457 problem details, which the import endpoint returns on a 422. */
interface ProblemDetails {
  title?: string
  detail?: string
  status?: number
  traceId?: string
}

/**
 * The policies a 200 body names.
 *
 * One PDF wording can resolve to more than one policy record — the same booklet published
 * as separate products, or under more than one brand — so the body is a plain array of
 * policies. It carries no findings: those are fetched per policy through the ordinary
 * `policies/{id}/findings` route once the user has said which of the matches to compare,
 * so an upload never pays for comparison text that is not going to be shown.
 */
export function normalizeImportedPolicies(body: unknown): IPolicy[] {
  if (!Array.isArray(body)) return []
  return body.filter(
    (entry): entry is IPolicy =>
      !!entry && typeof entry === 'object' && (entry as IPolicy).id != null,
  )
}

/**
 * Where the import endpoint is asked to send this wording, session expiry included.
 *
 * An imported policy is held against the user's account rather than added to the
 * catalogue, and that association has to end with the session that made it. The token is
 * opaque to the API, so the expiry only reaches it if the front end sends it. The API
 * caps it at 24 hours from receipt either way, so an unknown expiry is simply left out
 * rather than guessed at.
 */
function buildImportEndpoint(): string {
  const endpoint = `${PCI_API_URLS}policies/import`
  const expiry = auth.sessionExpiry
  if (!expiry) return endpoint
  return `${endpoint}?${new URLSearchParams({ expiry }).toString()}`
}

async function importPolicyPdf(file: File): Promise<PolicyImportResult> {
  const endpoint = buildImportEndpoint()

  const form = new FormData()
  form.append('file', file, file.name)

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { ...getAuthHeaders(), ...getImpersonationHeaders() },
    body: form,
  })

  if (res.status === 200) {
    const policies = normalizeImportedPolicies(await res.json().catch(() => null))

    // A 200 that names no policy has nothing to put in the comparison. That is the same
    // dead end as a 422 — recognised, but not turnable into a column — so it is reported
    // the same way rather than loading an empty table.
    if (!policies.length) {
      console.warn(`Policy import returned no policy for ${file.name}`)
      return { status: 'unprocessable', fileName: file.name }
    }

    return { status: 'found', policies, fileName: file.name }
  }
  if (res.status === 202) {
    return { status: 'not_found', fileName: file.name }
  }

  // 400 is a rule the user can do something about — a wording belonging to another
  // country, say — and the server states it in terms meant to be read, so the detail is
  // shown as-is rather than replaced with a generic failure.
  if (res.status === 400) {
    const problem = (await res.json().catch(() => null)) as ProblemDetails | null
    const detail = problem?.detail?.trim()
    return {
      status: 'rejected',
      fileName: file.name,
      detail: detail || 'This policy cannot be imported.',
    }
  }

  // 422 means the wording was recognised but the server could not turn it into a policy -
  // a matched hash pointing at a policy that no longer resolves, for instance. That is a
  // known outcome rather than a transport failure, so it comes back as a result. The
  // problem detail names internal ids and is only ever logged, never shown.
  if (res.status === 422) {
    const problem = (await res.json().catch(() => null)) as ProblemDetails | null
    console.warn(
      `Policy import could not process ${file.name}:`,
      problem?.detail ?? 'no detail provided',
      problem?.traceId ? `(trace ${problem.traceId})` : '',
    )
    return { status: 'unprocessable', fileName: file.name }
  }

  const text = await res.text().catch(() => '')
  throw new Error(`Policy import failed (${res.status}): ${text || 'Unknown error'}`)
}

export { importPolicyPdf }
